import { createContext, useContext, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const NavStateContext = createContext();

export const NavStateProvider = ({ children }) => {

  const location = useLocation();
  const navigate = useNavigate();

  const [refreshNavState, setRefreshNavState] = useState(false); // A dummy variable used to force navState to recalculate after assign() is called

  const navStateFromStorage = () => {
    const params = new URLSearchParams(location.search);
    const navStateId = params.get('navStateId');
    if (!navStateId) return {};

    const key = `navState${navStateId}`;
    let navStateStr = sessionStorage.getItem(key);

    if (!navStateStr) {
      // Check if this is a new tab with state passed through localStorage
      navStateStr = localStorage.getItem('newTabState');
      if (navStateStr) {
        // Clean up localStorage
        sessionStorage.setItem(key, navStateStr);
        localStorage.removeItem(key);
      }
    }

    return navStateStr ? JSON.parse(navStateStr) : {};
  };

  // Whenever the navStateId changes we must refresh the navState
  const navState = useMemo(() => {
    return navStateFromStorage();
  }, [location.search, refreshNavState]);

  const saveNavState = (newState) => {
    sessionStorage.setItem('navState' + newState.id, JSON.stringify(newState));
  };

  const assign = (key, obj) => {

    if (key === 'breadcrumb') {
      console.error('The breadcrumb method must be used to update the title');
      return;
    }

    // Always update based on sessionStorage in case assign() is called twice in the same render cycle
    const prevNavState = navStateFromStorage();
    const updatedNavState = { ...prevNavState, [key]: obj };
    saveNavState(updatedNavState);

    setRefreshNavState(prev => !prev);
  };

  const mode = navState.mode ?? 'LIST';

  // Navigation
  const navigateTo = (url, newState = {}, openNewTab = false) => {

    // Clear any future states
    if (!openNewTab) {
      let nextId = (navState.id ?? 0) + 1;
      while (sessionStorage.getItem('navState' + nextId)) {
        sessionStorage.removeItem('navState' + nextId);
        nextId++;
      }
    }

    let nextId = openNewTab ? 1 : (navState.id ?? 0) + 1;
    newState.id = nextId;
    newState.url = url;

    if (!openNewTab) {
      saveNavState(newState);
      // TODO: AH discuss this
      navigate({ pathname: url, search: `?navStateId=${nextId}` }, { state: newState }); 
    } else {
      const href = `${url}?navStateId=${nextId}`;
      localStorage.setItem('newTabState', JSON.stringify(newState));
      window.open(href, '_blank', 'noopener');
    }

  }

  return (
    <NavStateContext.Provider value={{ navState, assign, mode, navigateTo }}>
      {children}
    </NavStateContext.Provider>
  );
};

export const useNavState = () => useContext(NavStateContext);